import { FormEvent, useMemo, useState } from 'react';
import { encryptSecret, generateKeyBase64 } from './crypto';
import { GenerateSecretScreen } from './GenerateSecretScreen';
import { CopySecretScreen } from './CopySecretScreen';
import { RetrieveSecret } from './RetrieveSecret';

type CopyStatus = 'idle' | 'copied' | 'failed';

type AppState = {
  secretText: string;
  saveError: string;
  isSaving: boolean;
  retrievalUrl: string;
  copyStatus: CopyStatus;
};

function App() {
  const searchParams = useMemo(() => new URLSearchParams(window.location.search), []);
  const hashParams = useMemo(() => new URLSearchParams(window.location.hash.slice(1)), []);
  const secretId = searchParams.get('id');
  const encryptionKey = hashParams.get('key');

  const [state, setState] = useState<AppState>({
    secretText: '',
    saveError: '',
    isSaving: false,
    retrievalUrl: '',
    copyStatus: 'idle',
  });

  const { secretText, saveError, isSaving, retrievalUrl, copyStatus } = state;

  async function handleCreate(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setState((prev) => ({ ...prev, saveError: '', copyStatus: 'idle', retrievalUrl: '' }));

    if (!secretText.trim()) {
      setState((prev) => ({ ...prev, saveError: 'Please enter a secret before generating a link.' }));
      return;
    }

    if (secretText.length > 10000) {
      setState((prev) => ({ ...prev, saveError: 'Secret must be 10000 characters or less.' }));
      return;
    }

    setState((prev) => ({ ...prev, isSaving: true }));

    try {
      const key = await generateKeyBase64();
      const encryptedSecret = await encryptSecret(secretText, key);

      const response = await fetch('/secrets', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ encryptedSecret }),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Unexpected error while saving the secret');
      }

      const fullUrl = `${data.retrievalUrl}#key=${encodeURIComponent(key)}`;
      setState((prev) => ({ ...prev, secretText: '', retrievalUrl: fullUrl, copyStatus: 'idle' }));
    } catch (error) {
      setState((prev) => ({
        ...prev,
        saveError: error instanceof Error ? error.message : 'Failed to save secret',
      }));
    } finally {
      setState((prev) => ({ ...prev, isSaving: false }));
    }
  }

  async function copyUrl(url: string) {
    try {
      await navigator.clipboard.writeText(url);
      setState((prev) => ({ ...prev, copyStatus: 'copied' }));
      setTimeout(() => setState((prev) => ({ ...prev, copyStatus: 'idle' })), 2000);
    } catch {
      setState((prev) => ({ ...prev, copyStatus: 'failed' }));
      setTimeout(() => setState((prev) => ({ ...prev, copyStatus: 'idle' })), 2000);
    }
  }

  function resetCreateFlow() {
    setState({
      secretText: '',
      saveError: '',
      isSaving: false,
      retrievalUrl: '',
      copyStatus: 'idle',
    });
  }

  return (
    <div className="card" id="app">
      {secretId ? (
        <RetrieveSecret secretId={secretId} encryptionKey={encryptionKey} />
      ) : retrievalUrl ? (
        <CopySecretScreen
          retrievalUrl={retrievalUrl}
          copyStatus={copyStatus}
          onCopy={() => copyUrl(retrievalUrl)}
          onGenerateNew={resetCreateFlow}
        />
      ) : (
        <GenerateSecretScreen
          secretText={secretText}
          saveError={saveError}
          isSaving={isSaving}
          onSecretTextChange={se => setState((prev) => ({ ...prev, secretText: se }))}
          onSubmit={handleCreate}
        />
      )}
    </div>
  );
}

export default App;
